import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@jsq/ds/core";
import { useUpdatePackageStateMutation } from "#graphql/supergraphHooks";
import { useRequiredArenaId } from "./useRequiredArenaId";
import type { PackageState } from "#graphql/supergraphTypes";

export type UseUpdatePackageState = {
  setPublishState: (state: PackageState) => Promise<void>;
  /** True while a write for the package passed in is in flight (disables the
   *  publish control). A write for a package the user has since left does not
   *  raise it. */
  isUpdating: boolean;
};

const NO_WRITES: ReadonlySet<string> = new Set();

const SUCCESS_MESSAGE: Record<PackageState, string> = {
  PUBLISHED: "Package published.",
  UNPUBLISHED: "Package unpublished.",
};

/**
 * Wraps `updatePackageState`. The mutation is idempotent server-side, so a
 * repeated publish returns the same package and lands on the success path. The
 * payload returns the updated package in the PackageDetail shape, so Apollo
 * normalizes it into the cache by id and the slideout header updates from the
 * payload without a refetch. There is no optimistic local flip, so a failure has
 * nothing to roll back; `refetch` is only called on error, to resync from the
 * server in case the write lost a conflict and the local view is stale.
 *
 * The coverage grid is not nudged here: its cell does not carry publish state,
 * so a publish cannot change what that grid shows.
 *
 * @param arenaId Write-path arena id. Writes fire on user action, well after the
 * host app's arena resolves, so an absent id here is a programming error that
 * throws rather than silently no-ops.
 */
export function useUpdatePackageState(
  refetch: () => void,
  packageId: string | null,
  arenaId: number | undefined,
): UseUpdatePackageState {
  const getArenaId = useRequiredArenaId(arenaId);
  const [mutate] = useUpdatePackageStateMutation();
  // Which packages the writes belong to, not merely that one is in flight. The
  // slideout keeps this hook mounted while `packageId` changes underneath it,
  // so a bare boolean would disable the next package's control for a write that
  // was never about it. A single slot has the mirror fault: an earlier write
  // resolving would clear the next package's entry while its write is pending.
  const [writingPackageIds, setWritingPackageIds] = useState<ReadonlySet<string>>(NO_WRITES);
  // Synchronous re-entrancy guard: the state above lags a render behind, so two
  // rapid presses could both pass the disabled control and fire the mutation
  // twice. A ref updates immediately, so the second press is dropped.
  const inFlightRef = useRef<Set<string>>(new Set());
  // The package on screen, read at resolution time. The callback's own
  // `packageId` is the write's target and stays captured, so the two can be
  // compared to tell whether the user moved on mid-write.
  const visiblePackageIdRef = useRef(packageId);
  useEffect(() => {
    visiblePackageIdRef.current = packageId;
  }, [packageId]);

  const setPublishState = useCallback(
    async (state: PackageState): Promise<void> => {
      if (packageId !== null) {
        if (inFlightRef.current.has(packageId)) {
          return;
        }
        inFlightRef.current.add(packageId);
        setWritingPackageIds((ids) => new Set(ids).add(packageId));
      }
      try {
        // Write-path seam: a missing arena or package here is a programming
        // error. The throws land in the catch below, so the user still gets the
        // error toast instead of a silent no-op.
        const resolvedArenaId = getArenaId();
        if (packageId === null) {
          throw new Error("Package is not available");
        }
        await mutate({
          variables: { input: { arenaId: resolvedArenaId, packageId, state } },
        });
        toast.success(SUCCESS_MESSAGE[state]);
      } catch {
        // A failed write leaves the server as the source of truth; resync in
        // case the failure was a conflict and the local view is stale. Skipped
        // once the user has moved on: `refetch` reads whatever package is on
        // screen, so it would resync the wrong one and leave the failed one
        // stale anyway.
        toast.error("Couldn’t update this package. Please try again.");
        if (visiblePackageIdRef.current === packageId) {
          refetch();
        }
      } finally {
        if (packageId !== null) {
          inFlightRef.current.delete(packageId);
          setWritingPackageIds((ids) => {
            const next = new Set(ids);
            next.delete(packageId);
            return next;
          });
        }
      }
    },
    [getArenaId, mutate, packageId, refetch],
  );

  const isUpdating = packageId !== null && writingPackageIds.has(packageId);

  return useMemo(() => ({ setPublishState, isUpdating }), [setPublishState, isUpdating]);
}
